import React from 'react';
import PropTypes from 'prop-types';
import isEqual from 'lodash.isequal';
import '../scss/weekSelector.scss';
import {
  Button,
  TextInput,
  FormGroup,
  FormLabel,
} from 'carbon-components-react';
import Add16 from '@carbon/icons-react/lib/add/16';
import moment from 'moment';
import { v4 as uuid } from 'uuid';
import Combo from '../components/common/Combo';
import { api } from './api/api';
import { showNotification } from './notification/BaseNotification';
import WeekSelectorModifyModal from './WeekSelectorModifyModal';
import { PlannedWeekChangeType } from './Constants';

export default class WeekSelector extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      error: null,
      week: null,
      currentWeekNumber: null,
      quantity: 1,
      plannedWeeks: [],
      weeks: [],
      year: props.year,
      modifyModalVisible: false,
      modifyingWeeks: []
    };
    this.weeks = [];
  }

  async componentDidMount() {
    this.setWeeks();
  }

  componentWillReceiveProps(nextProps) {
    if (nextProps.year !== this.props.year) {
      this.setState({ year: nextProps.year }, () => {
        this.setWeeks();
      });
      this.props.onChange([]);
      this.clearInput();
    }
  }

  shouldComponentUpdate(nextProps, nextState) {
    return !isEqual(nextProps, this.props) || !isEqual(nextState, this.state);
  }

  async setWeeks() {
    const weeksForYear = (
      await api.getAll(`me/weeks/${this.state.year || moment().year()}`)
    ).data;
    const yearNow = moment().year();
    if (!this.props.editingDisabled) {
      if (this.state.year === yearNow) {
        const weekCurrent = moment().week();
        this.weeks = weeksForYear.filter((w) =>
          !this.props.items.find((i) => i.weekNumber === w.weekNum) && 
                                        w.weekNum >= weekCurrent - 1);
      } else {
        this.weeks = weeksForYear.filter((w) => 
          !this.props.items.find((i) => i.weekNumber === w.weekNum));
      }
    } else if (this.state.year === yearNow) {
        const weekNow = moment().week();
        if (this.props.items.length > 0) {
          this.weeks = weeksForYear.filter(({ weekNum: week }) =>
            this.props.items.some(
              (i) => week === i.weekNumber || week >= weekNow - 1
            )
          );
        } else {
          this.weeks = (weeksForYear || []).filter(
            (el) => el.weekNum >= weekNow - 1
          );
        }
    } else {
        this.weeks = [...weeksForYear];
    }
    this.setState({
      weeks: this.weeks,
      plannedWeeks: this.props.items.map((x) => ({
          quantity: x.quantity,
          weekNum: x.weekNumber,
          weekNumber: x.weekNumber,
          weekStart: weeksForYear?.filter((y) => y.weekNum === x.weekNumber)[0]
            ?.weekStart,
          weekFinish: weeksForYear?.filter((y) => y.weekNum === x.weekNumber)[0]
            ?.weekFinish,
        }), this),
    });
  }

  getKey = (item) => `${item.weekNumber}_${item.quantity}`

  clearInput = () => {
    this.setState({
      quantity: 1,
      currentWeekNumber: null,
      week: null,
    });
  };

  setError = (error) => {
    this.setState({ error });
  };

  addPlannedWeek = () => {
    if (
      this.state.currentWeekNumber != null &&
      this.state.quantity > 0 &&
      this.state.plannedWeeks.filter(
        (x) => x.weekNum === this.state.currentWeekNumber
      )?.length === 0
    ) {
      const weekS = this.weeks.filter(
          (y) => y.weekNum === this.state.currentWeekNumber
        )[0].weekStart;
      const weekF = this.weeks.filter(
          (y) => y.weekNum === this.state.currentWeekNumber
        )[0].weekFinish;
      const prevPlannedWeeks = this.state.plannedWeeks;
      prevPlannedWeeks.push({
        quantity: this.state.quantity,
        weekNumber: this.state.currentWeekNumber,
        weekNum: this.state.currentWeekNumber,
        weekStart: weekS,
        weekFinish: weekF
      });
      prevPlannedWeeks.sort((a, b) => a.weekNumber - b.weekNumber);
      this.setState((prevState) => {
        const prevWeeks = prevState.weeks;
        if (!this.props.editingDisabled) {
          prevWeeks.splice(prevWeeks.findIndex((e) => 
            e.weekNum === prevState.currentWeekNumber), 1);
        }
        
        return { plannedWeeks: prevPlannedWeeks, weeks: prevWeeks };
      });

      this.clearInput();
      this.props.onChange(prevPlannedWeeks);
      if (!this.props.editingDisabled) {
        for (let i = 1; i <= this.state.quantity; i += 1) {
          this.props.onSubmitPlannedWeekChange({ id: uuid(), 
                                                oldWeekNumber: this.state.currentWeekNumber, 
                                                oldQuantity: 0, 
                                                idDrillPlannedWeekChangeType: 
                                                  PlannedWeekChangeType.ToAdd, 
                                                weekNumber: this.state.currentWeekNumber, 
                                                weekStart: weekS, 
                                                weekFinish: weekF, 
                                                quantity: this.state.quantity, 
                                                sequence: i, 
                                                reasonForChange: null });
        }
      }
    } else if (this.state.currentWeekNumber) {
      showNotification.warning(
        `Warning: Week ${this.state.currentWeekNumber} already added to the list`
      );
    } else {
      showNotification.warning('Warning: Week is mandatory');
    }
  };

  handleDelete = (item) => {
    const array = this.state.plannedWeeks.filter(
      (i) => this.getKey(i) !== this.getKey(item)
    );
    this.setState((prevState) => {
      const prevWeeks = prevState.weeks;
      if (!this.props.editingDisabled) {
        prevWeeks.push({ name: item.weekNum.toString(), 
                         weekNum: item.weekNum, 
                         weekStart: item.weekStart, 
                         weekFinish: item.weekFinish });
      }
      return { plannedWeeks: array, weeks: prevWeeks };
    });
    this.props.onChange(array);
    if (!this.props.editingDisabled) {
      for (let i = 1; i <= item.quantity; i += 1) {
        this.props.onSubmitPlannedWeekChange({ id: uuid(), 
                                               oldWeekNumber: item.weekNum, 
                                               oldQuantity: item.quantity, 
                                               idDrillPlannedWeekChangeType: 
                                                PlannedWeekChangeType.ToDelete, 
                                               weekNumber: item.weekNumber, 
                                               weekStart: item.weekStart, 
                                               weekFinish: item.weekFinish, 
                                               quantity: 0, 
                                               sequence: i, 
                                               reasonForChange: null });
      }
    }
  };

  handleModify = (item) => {
    let mWeeks = [];
    if ((item !== undefined) && (item !== null)) {
      mWeeks = this.state.weeks.map((a) => ({ ...a }));
      const selectedItem = { name: item.weekNum.toString(), 
                             weekFinish: item.weekFinish, 
                             weekNum: item.weekNum, 
                             weekStart: item.weekStart };
      mWeeks.push(selectedItem);
    }
    this.setState(() => ({ modifyingWeeks: mWeeks, 
                           quantity: item?.quantity, 
                           currentWeekNumber: mWeeks.find((el) => el.weekNum === item.weekNum) }));
    this.setState((prevState) => ({ modifyModalVisible: !prevState.modifyModalVisible }));
  };

  handleModifyPlannedWeek = (item) => {
    if ((item !== null) && (item !== undefined)) {
      if (item.oldWeekNumber === item.weekNumber) {
        this.setState((prevState) => {
          const prevPlannedWeeks = prevState.plannedWeeks;
          const prevItemIdx = prevPlannedWeeks.findIndex((el) => el.weekNum === item.weekNumber);
          if (prevItemIdx !== -1) { prevPlannedWeeks[prevItemIdx].quantity = item.quantity; }
          return { plannedWeeks: prevPlannedWeeks };
        });
        const diffQuantity = item.quantity - item.oldQuantity;
        if (diffQuantity > 0) {
          for (let i = 1; i <= diffQuantity; i += 1) {
            this.props.onSubmitPlannedWeekChange({ id: uuid(), 
                                                   oldWeekNumber: item.oldWeekNumber, 
                                                   oldQuantity: item.oldQuantity, 
                                                   idDrillPlannedWeekChangeType:
                                                    PlannedWeekChangeType.ToAdd, 
                                                   weekNumber: item.weekNumber, 
                                                   weekStart: item.weekStart, 
                                                   weekFinish: item.weekFinish, 
                                                   quantity: item.quantity, 
                                                   sequence: i, 
                                                   reasonForChange: null });
          }
        } else {
          for (let i = 1; i <= -diffQuantity; i += 1) {
            this.props.onSubmitPlannedWeekChange({ id: uuid(), 
                                                   oldWeekNumber: item.oldWeekNumber, 
                                                   oldQuantity: item.oldQuantity, 
                                                   idDrillPlannedWeekChangeType:
                                                    PlannedWeekChangeType.ToDelete, 
                                                   weekNumber: item.weekNumber, 
                                                   weekStart: item.weekStart, 
                                                   weekFinish: item.weekFinish, 
                                                   quantity: item.quantity, 
                                                   sequence: i, 
                                                   reasonForChange: null });
          }
        }
      } else {
        this.setState((prevState) => {
          const prevPlannedWeeks = prevState.plannedWeeks;
          const array = prevPlannedWeeks.filter(
            (i) => this.getKey(i) !== this.getKey({ weekNumber: item.oldWeekNumber, 
                                                    quantity: item.oldQuantity })
          );
          const prevWeeks = prevState.weeks;
          array.push({
            quantity: item.quantity,
            weekNumber: item.weekNumber,
            weekNum: item.weekNumber,
            weekStart: prevWeeks.filter(
              (y) => y.weekNum === item.weekNumber
            )[0].weekStart,
            weekFinish: prevWeeks.filter(
              (y) => y.weekNum === item.weekNumber
            )[0].weekFinish,
          });
          array.sort((a, b) => a.weekNumber - b.weekNumber);
          const yearNow = moment().year();
          if (prevState.year === yearNow) {
            const weekCurrent = moment().week();
            if (item.oldWeekNumber >= weekCurrent - 1) {
              prevWeeks.push({ name: item.oldWeekNumber.toString(), 
                              weekNum: item.oldWeekNumber, 
                              weekStart: item.oldWeekStart, 
                              weekFinish: item.oldWeekFinish });
            }
          }
          prevWeeks.splice(prevWeeks.findIndex((e) => e.weekNum === item.weekNumber), 1);

          return { plannedWeeks: array, weeks: prevWeeks };
        });
        const diffQuantity = item.quantity - item.oldQuantity;
        if (diffQuantity > 0) {
          let i = 1;
          for (; i <= diffQuantity; i += 1) {
            this.props.onSubmitPlannedWeekChange({ id: uuid(), 
                                                   oldWeekNumber: item.oldWeekNumber, 
                                                   oldQuantity: item.oldQuantity, 
                                                   idDrillPlannedWeekChangeType: 
                                                    PlannedWeekChangeType.ToAdd, 
                                                   weekNumber: item.weekNumber, 
                                                   weekStart: item.weekStart, 
                                                   weekFinish: item.weekFinish, 
                                                   quantity: item.quantity, 
                                                   sequence: i, 
                                                   reasonForChange: null });
          }
          this.props.onSubmitPlannedWeekChange({ id: uuid(), 
                                                 oldWeekNumber: item.oldWeekNumber, 
                                                 oldQuantity: item.oldQuantity, 
                                                 idDrillPlannedWeekChangeType: 
                                                  PlannedWeekChangeType.ToChange, 
                                                 weekNumber: item.weekNumber, 
                                                 weekStart: item.weekStart, 
                                                 weekFinish: item.weekFinish, 
                                                 quantity: item.quantity, 
                                                 sequence: i, 
                                                 reasonForChange: null });
        } else if (diffQuantity < 0) {
          let i = 1;
          for (; i <= -diffQuantity; i += 1) {
            this.props.onSubmitPlannedWeekChange({ id: uuid(), 
                                                   oldWeekNumber: item.oldWeekNumber, 
                                                   oldQuantity: item.oldQuantity, 
                                                   idDrillPlannedWeekChangeType: 
                                                    PlannedWeekChangeType.ToDelete, 
                                                   weekNumber: item.weekNumber, 
                                                   weekStart: item.weekStart, 
                                                   weekFinish: item.weekFinish, 
                                                   quantity: item.quantity, 
                                                   sequence: i, 
                                                   reasonForChange: null });
          }
          this.props.onSubmitPlannedWeekChange({ id: uuid(), 
                                                 oldWeekNumber: item.oldWeekNumber, 
                                                 oldQuantity: item.oldQuantity, 
                                                 idDrillPlannedWeekChangeType: 
                                                  PlannedWeekChangeType.ToChange, 
                                                 weekNumber: item.weekNumber, 
                                                 weekStart: item.weekStart, 
                                                 weekFinish: item.weekFinish, 
                                                 quantity: item.quantity, 
                                                 sequence: i, 
                                                 reasonForChange: null });
        } else {
          this.props.onSubmitPlannedWeekChange({ id: uuid(), 
                                                 oldWeekNumber: item.oldWeekNumber, 
                                                 oldQuantity: item.oldQuantity, 
                                                 idDrillPlannedWeekChangeType: 
                                                  PlannedWeekChangeType.ToChange, 
                                                 weekNumber: item.weekNumber, 
                                                 weekStart: item.weekStart, 
                                                 weekFinish: item.weekFinish, 
                                                 quantity: item.quantity, 
                                                 sequence: 1, 
                                                 reasonForChange: null });
        }
      }
      this.props.onChange(this.state.plannedWeeks);
      this.setState(() => ({ modifyingWeeks: [], quantity: 1, currentWeekNumber: null }));
    }
    this.setState((prevState) => ({ modifyModalVisible: !prevState.modifyModalVisible }));
  }

  renderWeek = (x, isFull = false) => x == null
      ? ''
      : `Week: ${x?.weekNum} | ${moment
          .utc(x?.weekStart)
          .format('DD/MM/YY')} - ${moment
          .utc(x?.weekFinish)
          .format('DD/MM/YY')}${ 
          isFull ? ` | N\u00B0 of Drill: ${x?.quantity}` : ''}`;

  render() {
    return (
      <div>
        <div className="bx--row">
          <div className="bx--col-lg-6 bx--col-md-12">
            <FormGroup legendText="">
              <FormLabel className="subTitle">Week number *</FormLabel>
              <Combo
                dropUp
                onChange={(event) =>
                  this.setState((prevState) => {
                    const curState = prevState;
                    curState.week = event.selectedItem || null;
                    curState.currentWeekNumber =
                      event.selectedItem?.weekNum || null;
                    return { curState };
                  })
                }
                id={this.state.weeks?.toString()}
                initialSelectedItems={this.state.week}
                disabled={
                  (this.props.disabled && this.props.editingDisabled) ||
                  !this.state.year
                }
                items={this.state.weeks}
                itemToString={this.renderWeek}
                placeholder="Select"
              />
            </FormGroup>
            {this.state.error && (
              <p className="srv-validation-message">{this.state.error}</p>
            )}
          </div>
          <div className="bx--col-lg-2 bx--col-md-4">
            <FormGroup legendText="">
              <FormLabel className="subTitle">N&deg; of drill *</FormLabel>
              <TextInput
                id="reportNumber"
                labelText=""
                name="reportNumber"
                placeholder="Number of drills"
                value={this.state.quantity}
                onChange={(event) => {
                  const val = event.target.value;
                  this.setState((prevState) => {
                    const curState = prevState;
                    curState.quantity = val || 1;
                    return { curState };
                  });
                }}
                type="number"
                min="1"
                disabled={
                  (this.props.disabled && this.props.editingDisabled) ||
                  !this.state.year
                }
              />
            </FormGroup>
          </div>
          <div className="bx--col-lg-2 bx--col-md-4 addBtnPosition">
            <Button
              onClick={this.addPlannedWeek}
              className="addBtnStyle"
              renderIcon={Add16}
              disabled={
                (this.props.disabled && this.props.editingDisabled) ||
                !this.state.year
              }
            />
          </div>
        </div>

        <div className="weekSelector bx--row">
          {this.state.plannedWeeks.map((item) => (
            <div className="tag-item" key={item?.weekNum}>
              {this.renderWeek(item, true)}
              {this.props.editingDisabled ? null : (
                <button className="button" type="button" onClick={() => this.handleModify(item)}>
                  <svg
                    focusable="false"
                    preserveAspectRatio="xMidYMid meet"
                    aria-label="Edit Drill Plan"
                    aria-hidden="true"
                    xmlns="http://www.w3.org/2000/svg"
                    width="16"
                    height="16"
                    viewBox="0 0 32 32"
                    role="img"
                    className="bx--btn__icon"
                  >
                    <rect width="28" height="2" x="2" y="26" />
                    <path d="M25.4,9c0.8-0.8,0.8-2,0-2.8c0,0,0,0,0,0l-3.6-3.6c-0.8-0.8-2-0.8-2.8,0c0,0,0,0,0,0l-15,15V24h6.4L25.4,9z M20.4,4L24,7.6 l-3,3L17.4,7L20.4,4z M6,22v-3.6l10-10l3.6,3.6l-10,10H6z" />
                  </svg>
                </button>
              )}
              {this.props.disabled && this.props.editingDisabled ? null : (
                <button
                  type="button"
                  className="button"
                  onClick={() => this.handleDelete(item)}
                >
                  &times;
                </button>
              )}
            </div>
          ))}
        </div>
        <WeekSelectorModifyModal 
          modalVisible={this.state.modifyModalVisible} 
          handleModalVisibility={this.handleModify} 
          onElementSubmitted={this.handleModifyPlannedWeek} 
          selectableItems={this.state.modifyingWeeks} 
          selectedItem={this.state.currentWeekNumber} 
          selectedQuantity={this.state.quantity} 
          renderWeek={this.renderWeek} 
          editingDisabled={this.props.editingDisabled} 
        />
      </div>
      
    );
  }
}

WeekSelector.propTypes = {
  year: PropTypes.number.isRequired,
  editingDisabled: PropTypes.bool,
  disabled: PropTypes.bool.isRequired,
  onSubmitPlannedWeekChange: PropTypes.func,
  onChange: PropTypes.func.isRequired,
  items: PropTypes.arrayOf(PropTypes.shape({
    quantity: PropTypes.number,
    weekNum: PropTypes.number,
    weekNumber: PropTypes.number,
    weekStart: PropTypes.string,
    weekFinish: PropTypes.string,
  })).isRequired,
};

WeekSelector.defaultProps = {
  editingDisabled: true,
  onSubmitPlannedWeekChange: () => {}
};
